/**
 * Dashboard Business Logic Conditions
 * 
 * Rules for when to show/hide UI elements on the Dashboard/Home screen
 * based on the state of notices and advertisements.
 * 
 * LOGIC SUMMARY:
 * 1. If no notices → show "No notices" card
 * 2. If no adverts → show "Set up ad center" card
 * 3. If adverts but no notices → show "No notices" card
 * 4. If notices and no adverts → show "Set up ad center" card AND charts
 * 5. If notices and adverts → show charts (normal state)
 */

export interface DashboardState {
  hasNotices: boolean;
  hasAdverts: boolean;
  isLoadingNotices: boolean;
  isLoadingAdverts: boolean;
}

/**
 * Condition 1: Show "No Notices" Card
 * 
 * WHEN: 
 *   - No notices exist, OR
 *   - Has adverts but no notices
 * SHOW: "No notices yet" card with image and "Create Notice" button
 * HIDE: Charts, stats, and normal dashboard content
 */
export const shouldShowNoNoticesCard = (state: DashboardState): boolean => {
  if (state.isLoadingNotices) return false;
  // Show if no notices (regardless of adverts status)
  return !state.hasNotices;
};

/**
 * Condition 2: Show "Set Up Ad Center" Card
 * 
 * WHEN: 
 *   - No adverts exist (regardless of notices status), OR
 *   - Has notices but no adverts
 * SHOW: "Set Up Your Ad Center" card with image and setup button
 * POSITION: Between empty state section and stats panel
 * 
 * NOTE: This card shows when user has notices but hasn't set up ad center,
 *       OR when user has no notices but we want to promote ad center setup
 */
export const shouldShowAdCenterSetupCard = (state: DashboardState): boolean => {
  if (state.isLoadingNotices || state.isLoadingAdverts) return false;
  // Show if no adverts (regardless of notices status)
  return !state.hasAdverts;
};

/**
 * Condition 3: Show Charts (Notices Created & Distribution)
 * 
 * WHEN: Has notices AND (has adverts OR no adverts)
 * 
 * SPECIFICALLY:
 * - If notices AND no adverts → show charts + "Set up ad center" card
 * - If notices AND has adverts → show charts (normal state)
 * 
 * SHOW: 
 *   - "Notices Created (Last 7 Days)" chart
 *   - "Notice Types Distribution" chart
 * HIDE: When no notices (empty state)
 */
export const shouldShowCharts = (state: DashboardState): boolean => {
  if (state.isLoadingNotices) return false;
  // Show charts if has notices (regardless of adverts status)
  return state.hasNotices;
};

/**
 * Condition 4: Show Stats Panel
 * 
 * WHEN: Has notices (regardless of adverts status)
 * SHOW: Stats overview with user counts, notice counts, events, adverts
 * HIDE: When no notices (empty state)
 */
export const shouldShowStatsPanel = (state: DashboardState): boolean => {
  if (state.isLoadingNotices) return false;
  return state.hasNotices;
};

/**
 * Complete Dashboard Display Logic
 * 
 * DECISION TREE:
 * 
 * 1. Loading? → Show skeleton
 * 
 * 2. No notices?
 *    YES → Show "No notices" card (hide everything else)
 *    NO → Continue to step 3
 * 
 * 3. Has notices?
 *    YES → Continue to step 4
 * 
 * 4. No adverts?
 *    YES → Show:
 *          - Charts (Notices Created & Distribution)
 *          - "Set up ad center" card
 *          - Stats panel
 *    NO → Show:
 *          - Charts (Notices Created & Distribution)
 *          - Stats panel
 *          - Normal dashboard content
 */
export const getDashboardDisplayState = (state: DashboardState) => {
  return {
    showNoNoticesCard: shouldShowNoNoticesCard(state),
    showAdCenterSetupCard: shouldShowAdCenterSetupCard(state),
    showCharts: shouldShowCharts(state),
    showStats: shouldShowStatsPanel(state),
  };
};

/**
 * Example Usage:
 * 
 * const state = {
 *   hasNotices: notices?.length > 0,
 *   hasAdverts: activeAdverts > 0 || hasAcceptedCurrentVersion("shop"),
 *   isLoadingNotices,
 *   isLoadingAdverts,
 * };
 * 
 * const display = getDashboardDisplayState(state);
 * 
 * {display.showEmptyState && <NoNoticesCard />}
 * {display.showAdCenterSetup && <AdCenterSetupCard />}
 * {display.showCharts && <Charts />}
 * {display.showStats && <StatsPanel />}
 */
